import { useEffect, useRef, useState, type ReactElement } from 'react';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../ui/form-dialog';
import type { SolicitarAbastecimentoView, SolicitarParaLoteInput, SolicitarParaSetorInput } from '@pieltec/contracts';
import { useToast } from '../ui/toast';
import { abastecimentoApi, ErroAbastecimento } from '../../lib/estoque/abastecimento-api';

// Roda no coletor (tema dark-premium, fora de data-office-theme): só utilitários definidos na base.

export type ModoSolicitacaoMaterial = 'LOT_OP' | 'SETOR';

export interface CamposSolicitacaoMaterial {
  readonly material: string;
  readonly quantidade: string;
  readonly unidade: string;
}

export type ErrosSolicitacaoMaterial = Partial<Record<keyof CamposSolicitacaoMaterial, string>>;

export const REFERENCIA_NAO_INFORMADA = 'Referência não informada';
export const TEXTO_OFFLINE = 'Conexão necessária para confirmar esta ação.';
export const TEXTO_RESULTADO_INCERTO = 'Não foi possível confirmar o resultado desta ação. Consulte a situação atual antes de tentar novamente.';
export const TEXTO_SUCESSO = 'Solicitação de abastecimento criada.';
export const TEXTO_FALHA_PADRAO = 'Não foi possível registrar a solicitação.';
export const TEXTO_SEM_REFERENCIA = 'Não foi possível identificar a referência da solicitação. Reabra a solicitação pelo cartão ou pelo setor.';

const ROTULO_ESTADO: Readonly<Record<string, string>> = { SOLICITADA: 'A separar', PARCIALMENTE_SEPARADA: 'Em separação', SEPARADA: 'Separada' };

/** Descrição do sucesso: solicitação criada e o estado devolvido pelo servidor; nunca afirma entrega. */
export function descreverSolicitacaoCriada(solicitacao: Pick<SolicitarAbastecimentoView, 'estado'>): { readonly titulo: string; readonly descricao: string } {
  return { titulo: TEXTO_SUCESSO, descricao: `Situação atual: ${ROTULO_ESTADO[solicitacao.estado] ?? 'conforme o servidor'}.` };
}

const ORDEM_CAMPOS: readonly (keyof CamposSolicitacaoMaterial)[] = ['material', 'quantidade', 'unidade'];

/** Positivo por string decimal (vírgula ou ponto), sem parseFloat: pelo menos um dígito diferente de zero. */
function quantidadePositiva(texto: string): boolean {
  const normalizada = texto.trim().replace(',', '.');
  return /^\d+(\.\d+)?$/.test(normalizada) && /[1-9]/.test(normalizada);
}

/**
 * Erros por campo. O modo não acrescenta campos: a solicitação por setor nunca pede nem inventa lote,
 * e a referência de lote/OP vem do cartão, não do operador.
 */
export function validarSolicitacaoMaterial(campos: CamposSolicitacaoMaterial, _modo: ModoSolicitacaoMaterial): ErrosSolicitacaoMaterial {
  const erros: { -readonly [K in keyof CamposSolicitacaoMaterial]?: string } = {};
  if (campos.material.trim() === '') erros.material = 'Informe o material.';
  if (!quantidadePositiva(campos.quantidade)) erros.quantidade = 'Informe uma quantidade maior que zero.';
  if (campos.unidade.trim() === '') erros.unidade = 'Informe a unidade.';
  return erros;
}

export function primeiroCampoComErro(erros: ErrosSolicitacaoMaterial): keyof CamposSolicitacaoMaterial | null {
  return ORDEM_CAMPOS.find((campo) => erros[campo] !== undefined) ?? null;
}

export interface EntradaSolicitacaoMaterial {
  readonly loteId?: string;
  readonly opId?: string;
  readonly celulaCodigo?: string;
  readonly material: string;
  readonly quantidade: string;
  readonly unidade: string;
}

export interface ApiSolicitacaoMaterial {
  readonly solicitarLote: (input: SolicitarParaLoteInput) => Promise<SolicitarAbastecimentoView>;
  readonly solicitarSetor: (input: SolicitarParaSetorInput) => Promise<SolicitarAbastecimentoView>;
}

/** Guarda de envio concorrente: uma confirmação por vez. */
export interface GuardaEnvioSolicitacao { emAndamento: boolean }

export type ResultadoEnvioSolicitacao =
  | { readonly tipo: 'criada'; readonly solicitacao: SolicitarAbastecimentoView; readonly chave: null }
  | { readonly tipo: 'recusada'; readonly mensagem: string; readonly chave: null }
  | { readonly tipo: 'incerta'; readonly mensagem: string; readonly chave: string }
  | { readonly tipo: 'ignorada'; readonly chave: string | null };

function gerarChave(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

/**
 * Envia a solicitação ao servidor e devolve o próximo estado e a chave a manter.
 * Rede/5xx: resultado incerto, a chave é mantida para a próxima tentativa (replay idempotente no servidor).
 * 4xx e sucesso: a chave é descartada. Sem sucesso local: só `criada` após resposta do servidor.
 */
export async function enviarSolicitacao({ api, modo, entrada, chaveAtual, guarda }: {
  readonly api: ApiSolicitacaoMaterial;
  readonly modo: ModoSolicitacaoMaterial;
  readonly entrada: EntradaSolicitacaoMaterial;
  readonly chaveAtual: string | null;
  readonly guarda?: GuardaEnvioSolicitacao;
}): Promise<ResultadoEnvioSolicitacao> {
  if (guarda?.emAndamento) return { tipo: 'ignorada', chave: chaveAtual };
  const { loteId, opId, celulaCodigo } = entrada;
  const comum = { materialCodigo: entrada.material.trim(), quantidade: entrada.quantidade.trim().replace(',', '.'), unidade: entrada.unidade.trim() };
  if (modo === 'LOT_OP' ? !loteId || !opId : !celulaCodigo) return { tipo: 'recusada', mensagem: TEXTO_SEM_REFERENCIA, chave: null };
  const chave = chaveAtual ?? gerarChave();
  if (guarda) guarda.emAndamento = true;
  try {
    const solicitacao = modo === 'LOT_OP'
      ? await api.solicitarLote({ loteId: loteId as string, opId: opId as string, ...comum, chaveIdempotencia: chave })
      : await api.solicitarSetor({ celulaCodigo: celulaCodigo, ...comum, chaveIdempotencia: chave });
    return { tipo: 'criada', solicitacao, chave: null };
  } catch (causa) {
    const status = causa instanceof ErroAbastecimento ? causa.status : null;
    if (status === null || status >= 500) return { tipo: 'incerta', mensagem: TEXTO_RESULTADO_INCERTO, chave };
    return { tipo: 'recusada', mensagem: causa instanceof Error && causa.message.trim() !== '' ? causa.message : TEXTO_FALHA_PADRAO, chave: null };
  } finally {
    if (guarda) guarda.emAndamento = false;
  }
}

/** Cabeçalho fixo da referência: lote/OP ou setor, sem inventar lote quando a referência falta. */
export function SolicitarMaterialReferencia({ modo, referencia }: { readonly modo: ModoSolicitacaoMaterial; readonly referencia: string }): ReactElement {
  const texto = referencia.trim();
  return (
    <section aria-label="Referência da solicitação" className="min-w-0 rounded border border-border bg-muted p-4 text-sm text-foreground">
      <p className="text-xs text-muted-foreground">{modo === 'LOT_OP' ? 'Lote / OP' : 'Solicitação por setor'}</p>
      <p className="mt-1 min-w-0 break-words font-semibold">{texto === '' ? REFERENCIA_NAO_INFORMADA : texto}</p>
    </section>
  );
}

export function SolicitarMaterialResumo({ modo, referencia }: { readonly modo: ModoSolicitacaoMaterial; readonly referencia: string }): ReactElement {
  return <SolicitarMaterialReferencia modo={modo} referencia={referencia} />;
}

const CLASSE_CAMPO = 'mt-1 min-h-11 w-full min-w-0 rounded border border-border bg-card p-2 text-foreground';

function CampoSolicitacao({ nome, rotulo, valor, erro, decimal, onAlterar }: {
  readonly nome: keyof CamposSolicitacaoMaterial; readonly rotulo: string; readonly valor: string; readonly erro?: string; readonly decimal?: boolean; readonly onAlterar: (nome: keyof CamposSolicitacaoMaterial, valor: string) => void;
}): ReactElement {
  const id = `solicitar-material-${nome}`;
  const idErro = `${id}-erro`;
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-sm font-semibold text-foreground">{rotulo}</label>
      <input id={id} name={nome} value={valor} autoComplete="off" inputMode={decimal ? 'decimal' : undefined} aria-invalid={erro ? true : undefined} aria-describedby={erro ? idErro : undefined} onChange={(evento) => onAlterar(nome, evento.target.value)} className={CLASSE_CAMPO} />
      {erro && <p id={idErro} role="alert" className="mt-1 min-w-0 break-words text-sm text-destructive">{erro}</p>}
    </div>
  );
}

export interface SolicitarMaterialCorpoProps {
  readonly modo: ModoSolicitacaoMaterial;
  readonly referencia: string;
  readonly campos: CamposSolicitacaoMaterial;
  readonly erros: ErrosSolicitacaoMaterial;
  readonly erroGeral: string | null;
  readonly online: boolean;
  readonly onAlterar: (nome: keyof CamposSolicitacaoMaterial, valor: string) => void;
}

/** Corpo presentacional do diálogo (testável sem portal). */
export function SolicitarMaterialCorpo({ modo, referencia, campos, erros, erroGeral, online, onAlterar }: SolicitarMaterialCorpoProps): ReactElement {
  return (
    <div data-solicitacao-material="" className="min-w-0 space-y-4">
      <SolicitarMaterialReferencia modo={modo} referencia={referencia} />
      <CampoSolicitacao nome="material" rotulo="Material" valor={campos.material} erro={erros.material} onAlterar={onAlterar} />
      <CampoSolicitacao nome="quantidade" rotulo="Quantidade" valor={campos.quantidade} erro={erros.quantidade} decimal onAlterar={onAlterar} />
      <CampoSolicitacao nome="unidade" rotulo="Unidade" valor={campos.unidade} erro={erros.unidade} onAlterar={onAlterar} />
      {!online && <p id="solicitar-material-offline" role="alert" className="min-w-0 break-words text-sm text-foreground">{TEXTO_OFFLINE}</p>}
      {erroGeral && <p role="alert" className="min-w-0 break-words text-sm text-destructive">{erroGeral}</p>}
    </div>
  );
}

export function SolicitarMaterialAcoes({ enviando, online, onCancelar, onEnviar }: {
  readonly enviando: boolean; readonly online: boolean; readonly onCancelar: () => void; readonly onEnviar: () => void;
}): ReactElement {
  return (
    <>
      <button type="button" className="min-h-11 rounded border border-border bg-card px-4 text-foreground disabled:opacity-50" onClick={onCancelar} disabled={enviando}>Cancelar</button>
      <button type="button" aria-busy={enviando} aria-describedby={online ? undefined : 'solicitar-material-offline'} className="min-h-11 rounded bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50" onClick={onEnviar} disabled={enviando || !online}>{enviando ? 'Enviando…' : 'Enviar solicitação'}</button>
    </>
  );
}

export function SolicitarMaterialDialog({ aberto, onAbertoChange, loteId, opId, celulaCodigo, referencia, onSolicitado }: {
  readonly aberto: boolean; readonly onAbertoChange: (aberto: boolean) => void; readonly loteId?: string; readonly opId?: string; readonly celulaCodigo?: string; readonly referencia: string; readonly onSolicitado: () => void;
}): ReactElement {
  const modo: ModoSolicitacaoMaterial = loteId && opId ? 'LOT_OP' : 'SETOR';
  const [campos, setCampos] = useState<CamposSolicitacaoMaterial>({ material: '', quantidade: '', unidade: '' });
  const [erros, setErros] = useState<ErrosSolicitacaoMaterial>({});
  const [enviando, setEnviando] = useState(false); const [erroGeral, setErroGeral] = useState<string | null>(null);
  const toast = useToast();
  const chaveRef = useRef<{ readonly chave: string; readonly assinatura: string } | null>(null);
  const guardaRef = useRef<GuardaEnvioSolicitacao>({ emAndamento: false });
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => { const atualizar = () => setOnline(navigator.onLine); addEventListener('online', atualizar); addEventListener('offline', atualizar); return () => { removeEventListener('online', atualizar); removeEventListener('offline', atualizar); }; }, []);
  const alterar = (nome: keyof CamposSolicitacaoMaterial, valor: string): void => {
    setCampos((atual) => ({ ...atual, [nome]: valor }));
    setErros((atual) => (atual[nome] === undefined ? atual : { ...atual, [nome]: undefined }));
  };
  const enviar = async (): Promise<void> => {
    if (!online || guardaRef.current.emAndamento) return;
    const encontrados = validarSolicitacaoMaterial(campos, modo);
    const primeiro = primeiroCampoComErro(encontrados);
    setErros(encontrados);
    if (primeiro) { setErroGeral(null); document.querySelector<HTMLInputElement>(`[data-solicitacao-material] [name="${primeiro}"]`)?.focus(); return; }
    // A chave só é reutilizada para o mesmo conteúdo: alterar o pedido após resultado incerto gera nova chave.
    const assinatura = JSON.stringify([modo, loteId, opId, celulaCodigo, campos.material.trim(), campos.quantidade.trim().replace(',', '.'), campos.unidade.trim()]);
    const chaveAtual = chaveRef.current?.assinatura === assinatura ? chaveRef.current.chave : null;
    setEnviando(true); setErroGeral(null);
    try {
      const resultado = await enviarSolicitacao({ api: abastecimentoApi, modo, entrada: { loteId, opId, celulaCodigo, ...campos }, chaveAtual, guarda: guardaRef.current });
      if (resultado.tipo === 'ignorada') return;
      chaveRef.current = resultado.chave === null ? null : { chave: resultado.chave, assinatura };
      if (resultado.tipo === 'criada') {
        const texto = descreverSolicitacaoCriada(resultado.solicitacao);
        toast.success({ title: texto.titulo, description: texto.descricao });
        onAbertoChange(false); onSolicitado();
      } else setErroGeral(resultado.mensagem);
    } finally { setEnviando(false); }
  };
  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <FormDialogHeader><FormDialogTitle>Solicitar material</FormDialogTitle><FormDialogDescription>A solicitação só existe depois da confirmação do servidor.</FormDialogDescription></FormDialogHeader>
        <FormDialogBody><SolicitarMaterialCorpo modo={modo} referencia={referencia} campos={campos} erros={erros} erroGeral={erroGeral} online={online} onAlterar={alterar} /></FormDialogBody>
        <FormDialogFooter><SolicitarMaterialAcoes enviando={enviando} online={online} onCancelar={() => onAbertoChange(false)} onEnviar={() => void enviar()} /></FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
}
